import {
  Injectable,
  ComponentFactoryResolver,
  Type,
  ComponentFactory,
  ApplicationRef,
  Injector,
  EmbeddedViewRef,
  RendererFactory2,
  Renderer2,
  ComponentRef
} from '@angular/core';
import {ModalContainerComponent} from './modal-container.component';
import {Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {FocusService} from '../../services/focus.service';

export type ModalDialogClass = 'modal-lg' | 'modal-md' | 'modal-sm' | null;

interface InputOnlyOptions<T> {
  modalDialogClass?: ModalDialogClass;
  modalTitle?: string;
  modalLabel?: string;
  initialState?: T;
  focusOnCreateSelector?: string;
}

type InputOutputInitializer<T> = (containerInstance: ModalContainerComponent, instance: T) => void;

@Injectable()
export class ModalService {
  private containerComponentFactory: ComponentFactory<ModalContainerComponent>;
  private renderer2: Renderer2;
  private modalStack: HTMLElement[] = [];

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private applicationRef: ApplicationRef,
    private injector: Injector,
    rendererFactory2: RendererFactory2,
    private dipseFocusService: FocusService
  ) {
    this.containerComponentFactory = this.componentFactoryResolver.resolveComponentFactory(ModalContainerComponent);
    this.renderer2 = rendererFactory2.createRenderer(null, null);
  }

  private static isInitializer<T, U>(initializerOrOptions?: InputOutputInitializer<T> | InputOnlyOptions<U>): initializerOrOptions is InputOutputInitializer<T> {
    return typeof initializerOrOptions === 'function';
  }

  create<T, U>(componentType: Type<T>, optionsOrInitializer: InputOnlyOptions<U> | InputOutputInitializer<T>, focusableAfterClose: HTMLElement | string): any {
    if (ModalService.isInitializer<T, U>(optionsOrInitializer)) {
      return this.initialize(componentType, optionsOrInitializer, focusableAfterClose);
    } else {
      this.initialize<T, void>(componentType, (containerInstance, instance) => {
        if (optionsOrInitializer.modalTitle) {
          containerInstance.modalTitle = optionsOrInitializer.modalTitle;
        }
        if (optionsOrInitializer.modalLabel) {
          containerInstance.modalLabel = optionsOrInitializer.modalLabel;
        }
        containerInstance.focusOnCreateSelector = optionsOrInitializer.focusOnCreateSelector;
        containerInstance.modalDialogClass = optionsOrInitializer.modalDialogClass;
        Object.assign(instance, optionsOrInitializer.initialState);
      }, focusableAfterClose).pipe(take(1)).pipe(take(1)).subscribe();
    }
  }

  createFromViewRef(
    viewRef: EmbeddedViewRef<any>,
    modalTitle: string,
    modalLabel: string,
    modalDialogClass: ModalDialogClass,
    focusOnCreateSelector?: string,
    closeOnBackdropClick?: boolean,
    closeLabel?: string
  ) {
    const containerComponentRef = this.containerComponentFactory.create(this.injector, [
      viewRef.rootNodes
    ]);
    this.handleFocusTrapping(containerComponentRef);
    this.handleBodyClass();
    this.applicationRef.attachView(containerComponentRef.hostView);
    containerComponentRef.instance.modalTitle = modalTitle;
    containerComponentRef.instance.modalLabel = modalLabel;
    containerComponentRef.instance.modalDialogClass = modalDialogClass;
    containerComponentRef.instance.focusOnCreateSelector = focusOnCreateSelector;
    containerComponentRef.instance.closeOnBackdropClick = closeOnBackdropClick;
    containerComponentRef.instance.closeLabel = closeLabel;
    containerComponentRef.instance.destroy.asObservable().pipe(take(1)).subscribe(() => {
      viewRef.destroy();
      containerComponentRef.destroy();
      this.handleFocusTrapping();
      this.handleBodyClass();
    });
    document.body.appendChild(containerComponentRef.location.nativeElement);
    return containerComponentRef.instance.destroy;
  }

  private initialize<T, U>(
    componentType: Type<T>,
    stateInitializer: InputOutputInitializer<T>,
    focusableAfterClose: HTMLElement | string
  ) {
    return new Observable<U>(observer => {
      const componentFactory = this.componentFactoryResolver.resolveComponentFactory<T>(componentType);
      const componentRef = componentFactory.create(this.injector);
      const containerComponentRef = this.containerComponentFactory.create(this.injector, [
        [componentRef.location.nativeElement]
      ]);
      this.handleFocusTrapping(containerComponentRef);
      this.handleBodyClass();

      if (focusableAfterClose instanceof HTMLElement) {
        containerComponentRef.instance.modalTitle = focusableAfterClose.getAttribute('modal-title');
        containerComponentRef.instance.modalLabel = focusableAfterClose.getAttribute('modal-label');
      }

      stateInitializer(containerComponentRef.instance, componentRef.instance);
      this.applicationRef.attachView(componentRef.hostView);
      this.applicationRef.attachView(containerComponentRef.hostView);
      containerComponentRef.instance.destroy.asObservable().pipe(take(1)).subscribe((v: U) => {
        componentRef.destroy();
        containerComponentRef.destroy();
        observer.next(v);
        observer.complete();

        this.handleFocusAfterClose(focusableAfterClose);
        this.handleFocusTrapping();
        this.handleBodyClass();
      });
      document.body.appendChild(containerComponentRef.location.nativeElement);
    });
  }

  private handleBodyClass() {
    (this.modalStack.length ? this.renderer2.addClass : this.renderer2.removeClass).call(this.renderer2, document.querySelector('html'), 'modal-open');
  }

  private handleFocusTrapping(containerComponentRef?: ComponentRef<ModalContainerComponent>) {
    if (containerComponentRef) {
      this.modalStack.push(containerComponentRef.instance.elementRef.nativeElement);
      this.dipseFocusService.trap(containerComponentRef.instance.elementRef.nativeElement);
    } else {
      this.modalStack.pop();
      if (this.modalStack.length) {
        this.dipseFocusService.trap(this.modalStack[this.modalStack.length - 1]);
      } else {
        this.dipseFocusService.unTrap();
      }
    }
  }

  private handleFocusAfterClose(focusableAfterClose: HTMLElement | string) {
    if (focusableAfterClose instanceof HTMLElement) {
      focusableAfterClose.focus();
    } else if (focusableAfterClose) {
      const element = document.body.querySelector<HTMLElement>(focusableAfterClose);
      if (element) {
        element.focus();
      }
    }
  }
}
